import { ROLE } from '../utils/roles';
import type { HelpGuide } from './types';

const shot = (file: string) => `/help/engineer/${file}`;

export const engineerGuide: HelpGuide = {
  id: 'engineer',
  title: 'Engineer',
  roles: [ROLE.ENG],
  intro:
    'These steps follow the screens an engineer uses every week. Open a task, match the picture, then click the control named in the caption.',
  tasks: [
    {
      id: 'sign-in',
      title: 'Sign in',
      goal: 'Open the workspace with the email and password you were given.',
      steps: [
        {
          instruction:
            'Go to the workspace login page. You should see the Nautilus Dynamics mark and the heading Secure Workspace Access.',
          image: shot('01-login.png'),
          caption: 'Login card with IDENTITY / EMAIL and ACCESS KEY / PASSWORD',
        },
        {
          instruction:
            'Type your work email in IDENTITY / EMAIL and your password in ACCESS KEY / PASSWORD. Leave the password hidden. Press INITIATE SESSION.',
          image: shot('01-login.png'),
          caption: 'INITIATE SESSION sits under the password field',
        },
      ],
    },
    {
      id: 'home',
      title: 'Find your way around',
      goal: 'Learn the home screen, the sidebar, and how to open a page.',
      steps: [
        {
          instruction:
            'After sign-in you land on Home. Widgets here show actions waiting for you, your timesheet, time off, and recent projects. The Nautilus mark at the top left always brings you back here.',
          image: shot('02-home.png'),
          caption: 'Home dashboard after sign-in',
        },
        {
          instruction:
            'Open the sidebar with the stacked-line control next to the mark. Sections such as PMO, HR, and Other Tools expand to show pages you are allowed to use. If a page is missing, you do not have that capability.',
          image: shot('03-sidebar.png'),
          caption: 'Sidebar with the sections an engineer can open',
        },
      ],
    },
    {
      id: 'profile',
      title: 'Set up your profile',
      goal: 'Add a photo, pick a theme, turn on notifications, and change your password.',
      steps: [
        {
          instruction:
            'Click your avatar at the far right of the top bar. Choose Details. That opens Personnel Record.',
          image: shot('04-user-menu.png'),
          caption: 'User menu with Details and Logout',
        },
        {
          instruction:
            'On IDENTITY, click the camera on your portrait to upload a photo. COMMUNICATION UPLINK turns browser notifications on or off. INTERFACE CONFIGURATION switches STANDARD MODE and DEEP OCEAN MODE. SECURITY PROTOCOLS is where you change your password — type the current key, then the new one, and save. Do not photograph password fields.',
          image: shot('05-profile.png'),
          caption: 'Personnel Record: identity, notifications, theme, password',
        },
      ],
    },
    {
      id: 'projects',
      title: 'Open a project',
      goal: 'Find a project you are assigned to and open its documents.',
      steps: [
        {
          instruction:
            'In the sidebar, open PMO, then Project List. The table lists projects where you are the project manager, an assigned engineer, or in an assigned department.',
          image: shot('06-projects.png'),
          caption: 'Projects Management table',
        },
        {
          instruction:
            'Click a project row. That opens Project Documents for that project.',
          image: shot('07-documents.png'),
          caption: 'Project Documents after choosing a project',
        },
      ],
    },
    {
      id: 'documents',
      title: 'Work with documents',
      goal: 'Read the document list, open a document’s revisions, and complete originator, checker, or approver actions.',
      steps: [
        {
          instruction:
            'The Project Documents tab is the list of documents on the project. Tabs along the top switch to Transmittals and, when you have access, CFOS.',
          image: shot('07-documents.png'),
          caption: 'Project Documents, Transmittals, and CFOS tabs',
        },
        {
          instruction:
            'Double-click a document row to open Document Revisions for that file. Each revision is a card (for example IFA1 or IFR1) with files plus three buttons at the bottom: History, Workflow, and Actions. The status line on the card tells you whose turn it is. Use the breadcrumb Documents to return to the list. Workflow is usually locked for engineers — only an admin, director, or the project manager can edit it, and never on an approved revision.',
          image: shot('08-revisions.png'),
          caption: 'Document Revisions cards with History, Workflow, and Actions',
        },
        {
          instruction:
            'History opens Document Audit History for that revision. It is a timeline of recorded events (created, uploaded, updated, deleted) with who did it, when, and any note. If a past version still has a file, a download control appears on that event. This is read-only. Close History returns you to the revision cards.',
          image: shot('08-history.png'),
          caption: 'Document Audit History timeline — Close History returns to the cards',
        },
        {
          instruction:
            'Actions opens Revision Actions. Which tabs you see depends on your place in the workflow and whose turn it is — if a tab is missing, it is not your step. Originator (author) sees Upload and Forward. Checker and approver see Validate, Reject, and Forward once a file is already there. Forward assigns a provisional user to take your step once, then you resume. Close leaves the revision unchanged. Do not press Finish Task, Validate, or Reject unless you mean to move the workflow. Approved revisions have no working tabs here.',
          image: shot('08-actions-originator.png'),
          caption: 'Revision Actions — tabs match your workflow role',
        },
        {
          instruction:
            'Originator. When the card says Awaiting first upload by Originator (or the revision was rejected back to you), you are the author. Stay on Upload. Notes are optional. Main Document is the file that counts: drop a PDF, DOC, or DOCX (Office files are converted; PDFs are stamped). You may need Check Preview before you can finish. Attachments go out with the revision. Internal Archives stay internal. Comments Response Sheet (CRS) is for answering comments on a later pass. Finish Task sends the version and starts or restarts the workflow — it stays disabled until a main file is ready. Close discards the dialog without sending.',
          image: shot('08-actions-originator.png'),
          caption: 'Originator Upload — Main Document, Attachments, Archives, CRS, then Finish Task',
        },
        {
          instruction:
            'Checker. When it is your turn as checker, Actions shows Validate, Reject, and Forward. Document Review sits above the tabs. You must press Review on the main PDF before Validate or Reject will work — those buttons stay disabled until that file shows Reviewed. Download other files (native or attachments) as needed; that does not replace Review. On Validate, add an optional comment and press Validate. That passes the revision to the approver.',
          image: shot('08-actions-checker.png'),
          caption: 'Checker Validate — Review the main PDF, then Validate (optional comment)',
        },
        {
          instruction:
            'Review opens the main PDF in the viewer. Use View to read. Annotate, Shapes, and Insert mark the page. Download saves a copy. Reject with comments attaches your marked-up PDF as a CRS on the Reject tab and returns you to Actions. The X closes the viewer when you are only reading. You still need this Review step before Validate or Reject will enable.',
          image: shot('08-actions-review-pdf.png'),
          caption: 'PDF Review — View, Annotate, Download, Reject with comments',
        },
        {
          instruction:
            'Reject (checker or approver). Rejecting resets the workflow so the originator must upload again. Type a Rejection Reason — it is required. You can attach a commented PDF from Reject with comments; it appears under CRS (Comment Response Sheet). Add Attachments if needed. Internal Archives are not shared with the client. Reject sends the rejection. Close leaves the revision unchanged.',
          image: shot('08-actions-reject.png'),
          caption: 'Reject — reason required, optional CRS and attachments, then Reject',
        },
        {
          instruction:
            'Approver. The dialog is the same as the checker’s: Document Review, then Validate, Reject, and Forward. The copy says As an approver. You still must Review the main PDF first. Validate is the green control and is the last internal step — after you validate, the revision is approved. Use Reject the same way as a checker if the file is not ready.',
          image: shot('08-actions-approver.png'),
          caption: 'Approver Validate — Review first, then the green Validate control',
        },
      ],
    },
    {
      id: 'transmittals',
      title: 'Check transmittals',
      goal: 'See packages of documents that were sent to the client.',
      steps: [
        {
          instruction:
            'On Project Documents, click the Transmittals tab. Each row is a sent package. Open a row to see which files went out and who received them. Engineers typically view transmittals; sending them is a document-controller or PM action.',
          image: shot('09-transmittals.png'),
          caption: 'Transmittals tab on a project',
        },
      ],
    },
    {
      id: 'timesheet',
      title: 'Fill your timesheet',
      goal: 'Enter hours for the week, save a draft, and open the submit dialog without sending it.',
      steps: [
        {
          instruction:
            'In the sidebar, open HR, then Timesheet. You land on My Timesheet for the current week. Use Add Row, pick a project, work package, and work unit, then type hours in the day cells.',
          image: shot('10-timesheet.png'),
          caption: 'My Timesheet with Add Row, Save Timesheet, and Submit for Validation',
        },
        {
          instruction:
            'Save Timesheet keeps a draft you can still edit. Submit for Validation opens the Submit Timesheet dialog. A warning appears if the week total does not match contracted hours. Add an optional comment if you need one. For this walkthrough, press Cancel — once you submit, only a PM rejection unlocks the week again.',
          image: shot('11-timesheet-submit.png'),
          caption: 'Submit Timesheet dialog — Cancel leaves the week as a draft',
        },
      ],
    },
    {
      id: 'time-off',
      title: 'Request time off',
      goal: 'Open a leave request from the calendar without sending it.',
      steps: [
        {
          instruction:
            'In the sidebar, open HR, then Calendar. The month view shows public holidays and leave already on the books.',
          image: shot('12-calendar.png'),
          caption: 'Calendar (time off) month view',
        },
        {
          instruction:
            'Click one or more days you want off. Request Time Off appears. It opens the Request Time Off form (full day or half day). Fill the type and dates, then press Cancel unless you mean to send the request.',
          image: shot('13-time-off-request.png'),
          caption: 'Request Time Off form — Cancel discards the draft',
        },
      ],
    },
    {
      id: 'pdf-editor',
      title: 'Annotate a PDF',
      goal: 'Open the PDF Editor and know how to load a file.',
      steps: [
        {
          instruction:
            'In the sidebar, open Other Tools, then PDF Editor. Drag a PDF onto the dashed area, or press Choose PDF or Open PDF. You can mark the file and place signature spaces, then send it for signing. Do not upload a live project file while you are only learning the screen.',
          image: shot('14-pdf-editor.png'),
          caption: 'PDF Editor empty drop zone with Choose PDF, Open PDF, and Send for signature',
        },
      ],
    },
    {
      id: 'cfos',
      title: 'Use CFOS',
      goal: 'Browse shared project files without uploading anything.',
      steps: [
        {
          instruction:
            'In the sidebar, open Other Tools, then CFOS. The Sandbox control chooses Shared or a project tree. Use Browse to walk folders, Search to find a file, and Upload only when you intend to add a file. Stay on Browse while you learn the layout.',
          image: shot('15-cfos.png'),
          caption: 'CFOS with Sandbox, Browse, Search, and Upload',
        },
      ],
    },
    {
      id: 'plane',
      title: 'Open Plane',
      goal: 'Reach the Plane board from Other Tools when that item is on your menu.',
      steps: [
        {
          instruction:
            'In the sidebar, open Other Tools. Plane opens the company Plane site in a new browser tab. Use it for issues and boards that live outside this workspace. Help (this guide) sits in the same section.',
          image: shot('16-other-tools.png'),
          caption: 'Other Tools in the sidebar, including Plane, PDF Editor, and CFOS',
        },
      ],
    },
  ],
};
